/*global $:false */

import { format as formatValue, getIndexFieldName } from "../helpers/recordm.value-formatter";
import ErrorType from '../helpers/error-type.js';
import FieldErrorM from '../helpers/model.field-error.js';
import { sprintf } from 'sprintf-js';

import ui from '@cob/ui-genesis/cob.ui.js';
import utils from '@cob/ui-genesis/cob.utils.js';
import validator from 'validator';

// Base entity Model intended to be extended for the specific entity models.
// Should be seen more like an abstract class and is not to be instanciated.
//
// The following members should be added directly to the constructor of the entitiy models that extend this
//
//  * entityKey
//  * filesEntityName
//  * columns
//  * get : this should be equal to `BaseM.buildGetFor('<entityKey>')` (see below)
//
// The following members should be added to the prototype of the entitiy models that extend this Base Model
//
//  * fieldValidationProps
var BaseM = function(core) {
    this._core = core;
    this.data = undefined;
    this.perms = undefined;
};

// types for entity fields
BaseM.fieldDataType = {
    STRING: "STRING",
    STRING_PHRASE: "STRING_PHRASE",
    NUMBER: "NUMBER",
    DATE: "DATE",
    DATETIME: "DATETIME",
    MULTIVALUE: "MULTIVALUE",
    TASKS: "TASKS",
    PARENT_CHILD: "PARENT_CHILD"
};

// for every $*ref field there may be a twin with the "_reference" suffix that contains a value to display.
// if there is a field description and the field is not "_formatted", then format its value, otherwise simply return it
BaseM.getReadableFieldValue = function (instanceData, fieldName, fieldDescription, fieldDefId, locale){
    const dataFieldName = getIndexFieldName(fieldName);

    // If the field does not exist, neither do it's "_formatted" or "_reference" forms.
    if(instanceData[dataFieldName] === undefined || instanceData[dataFieldName].length === 0){
        return "";
    }

    // exclude $date* fields so that we get the new locale-aware formatting even without re-indexing
    if (!(/\$datetime/).test(fieldDescription) && !(/\$date/).test(fieldDescription)) {
        if(instanceData[`${dataFieldName}_formatted`] !== undefined){
            return instanceData[`${dataFieldName}_formatted`].join(", ");
        }
    }

    let fieldValue = instanceData[`${dataFieldName}_reference`] ?? instanceData[dataFieldName];

    // Need to check for Arrays because external entity field values may not be arrays
    // and in those cases we just return the raw value
    if(Array.isArray(fieldValue) && fieldValue.length > 0){
        if(fieldDescription !== undefined){
            // Filter non-null values because $tasks fields return null for completed tasks so that they don't get shown
            fieldValue = fieldValue.map((value, idx) => {
                const rawValue = instanceData[dataFieldName][idx];
                return formatValue(value, rawValue, fieldDescription, fieldDefId, instanceData.instanceId, locale);
            }).filter((val) => val !== null);
        }

        return fieldValue.join(", ");
    }

    return fieldValue ?? "";
}

// ## Interaction with the server and the ModelRepository

// ### BaseM.buildGetFor
//
// Build a getter for a specific entity.
// The getter will return an instance from the ModelRepository or from the server, if necessary.
// it accepts an optional `opts` argument that can have the following properties:
//
// * `forceReload` - if `true`, we will force the reload of any instance that is already in the repo
// * `instanceURI` - optionally we can pass an URI to the instance that we want to load, instead of building it from
//                   the id. It's used for ExternalInstanceMs that don't respect RecordM's URI conventions.
// * `accepts` - optionally we can pass the contentType that we want from the server.
//                   Used first on ExternalInstanceMs.
// * `success` - optional success callback, called with the instance. It's called only once, ignoring `forceReload`
// * `error` - optional error callback, called with the error status.
BaseM.buildGetFor = function(constructor) {

    // #### get
    //
    // Tries to get the entity from the ModelRepository, falling back to loading from the server.
    // **This is the function that will be available on the Entity Models that extend this.**
    var get = function(core, id, opts) {
        //first we check the modelrepo
        var cachedEntityM = core.get_and_subscribe(constructor.entityKey + ":" + id),
            _opts = opts || {};

        if (cachedEntityM) {
            //TODO JONE pensar se faz sentido lançar um evento se a entity já foi carregada anteriormente.
            core.publish(cachedEntityM.loaded_event_name, cachedEntityM);

            if (_opts.success && !_opts.forceReload) _opts.success(cachedEntityM);

            if (_opts.forceReload) cachedEntityM.reload(opts);

        } else {
            // if the entity isn't in the repo, lets load from the server. (we don't need to call reload)
            _loadEntity(core, id, _opts);
        }

    };

    // #### _loadEntity
    //
    // When the instance isn't in the ModelRepository, this is called to load it from the server.
    var _loadEntity = function(core, id, opts) {
        var endpointPath = opts.instanceURI || 'recordm/' + constructor.entityKey + '/' + id;

        $.ajax({
            url: endpointPath,
            type: 'GET',
            //this is a little confusing, but I believe that jQuery uses the value of dataType
            //as the key to the accepts object. So we need both. The dataType to say that we are
            // expecting json, and the accepts to specify the "Accepts" header value for json.
            dataType: 'json',
            accepts: {
                json: opts.accepts || "application/json"
            },
            xhrFields: {
                withCredentials: true
            },
            success: function(data) {
                var entityM = new constructor(core, data);

                core.add_and_subscribe(constructor.entityKey + ":" + data.id, entityM);

                core.publish(entityM.loaded_event_name, entityM);

                if (opts.success) opts.success(entityM);
            },
            error: function(req) {
                window.console.log("ENTITY:" + constructor.entityKey + ":error: " +
                                   req.status + " : " + req.statusText + ":" + req.responseText);

                if (opts.error) opts.error(req.status, req);
            }
        });
    };

    return get;
};

//Returns the entity type
BaseM.prototype.getEntityKey = function() {
    return this._entityKey;
};

BaseM.prototype.getEntityName = function() {
    return this._entityName;
};

//Returns the entity Description
BaseM.prototype.getDescription = function() {
    return this.data.id;
};

//Returns the entity Description
BaseM.prototype.getId = function() {
    return this.data.id;
};

BaseM.prototype.getUri = function(){
    return this.uri;
};

// ### BaseM.prototype.reload
//
// Reloads an entity from the backend. If, and only if, the entity version has changed on the server,
// updates the current cached entity data and sends a `recordm:loaded:<entityKey>` event.
BaseM.prototype.reload = function(opts) {
    var _this = this;
    var endpointPath = opts.instanceURI || 'recordm/' + this._entityKey + '/' + this.data.id;
    var cachedEntityVersion = this.data ? _this.data.version : undefined;

    $.ajax({
        url: endpointPath,
        type: 'GET',
        contentType: "application/json",
        xhrFields: {
            withCredentials: true
        },
        beforeSend: function(xhr) {
            if (cachedEntityVersion !== undefined) {
                xhr.setRequestHeader('If-None-Match', cachedEntityVersion);
            }
        },
        success: function(data, textStatus, jqXHR) {
            //if the entity was up to date, do not update the entity data and do not send the load event.
            if (jqXHR.status !== 304) {
                window.console.log("Entity version has changed. New version loaded");
                _this.data = data;
                _this._core.publish(_this.loaded_event_name, _this);
            }

            if (opts.success) opts.success(_this);
        },
        error: function(req) {
            window.console.log("ENTITY:" + _this._entityKey + ":error: " +
                               req.status + " : " + req.statusText + ":" + req.responseText);

            if (opts.error) opts.error(req.status);
        }
    });
};

// ### BaseM.prototype.loadPerms
//
// loads the specific instance Perms from the server.
BaseM.prototype.loadPerms = function(cb) {
    var _this = this;

    if (this.perms !== undefined) {
        //we already loaded the perms
        cb();

    } else if (this.data._links && this.data._links.perms) {
        //if there's a _links.perms field load the perms
        $.ajax({
            url: this.data._links.perms,
            type: 'GET',
            contentType: "application/json",
            success: function(data) {
                _this.perms = data;
                cb();
            }

        });

    } else {
        //there are no perms
        cb();
    }
};

// ### BaseM.prototype.sync
//
// A proxy function that will sync the data with the server.
// if the data has no id then it will assume that it is a new record
// otherwise it will call the update
//
// * `data` - the updated data to save
// * `opts` - optional callbacks: `success`, `error` and `fieldError`
BaseM.prototype.sync = function(data, opts) {

    if (data.id > 0) {
        this.update(data, opts);

    } else {
        this.save(data, opts);
    }
};

// ### BaseM.prototype.update
//
// Save the entity on the server.
//
// * `data` - the updated data to save
// * `opts` - optional callbacks: `success`, `error` and `fieldError`
BaseM.prototype.save = function(data, opts) {
    var _this = this,
        _opts = opts || {};

    const url = new URL("recordm/" + _this._entityKey, window.location.href);
    if(_opts.params){
       for(const [key, value] of Object.entries(_opts.params)){
          url.searchParams.append(key, value);
       }
    }

    $.ajax({
        url: url.toString(),
        type: 'POST',
        contentType: "application/json",
        data: JSON.stringify(data),
        ignoreErrors: _opts.ignoreErrors,
        success: function(responseData) {

            if (responseData && responseData._links) {
                // Updating our model with the one from the server
                _this.data = responseData;

            } else {
                // If everything went ok while updating the entity then we can use the current entity state
                // The entity will be updated the next time we make a search or when the entity is beeing edited.
                _this.data = data;
                _this.data.id = responseData.id;
            }

            _this._core.addModel(_this._entityKey + ":" + _this.data);

            _this._core.publish(_this.created_event_name, _this.data);

            if (_opts.success) _opts.success();
        },
        error: function(req) {
            window.console.log("ENTITY:" + _this._entityKey + ":error-save: " +
                               req.status + " : " + req.statusText + ":" + req.responseText);

            if (req.status === 409) {
                ui.notification.showCustom(
                    'error',
                    sprintf(_this._core.translateString(_this._i18nKey, _this._i18nPrefix + ".msg.error-title"), _this.getDescription()),
                    _this._core.translateString("recordm.error.codes", "generic.409")
                );

            } else if (req.status === 403) {
                ui.notification.showCustom(
                    'error',
                    sprintf(_this._core.translateString(_this._i18nKey, _this._i18nPrefix + ".msg.error-title"), _this.getDescription()),
                    _this._core.translateString("recordm.error.codes", "generic.403")
                );

            } else if (req.status === 400 && _opts.fieldError) {
                _opts.fieldError(JSON.parse(req.responseText));

            } else {
                ui.notification.showNotificationsFromRequest('error', req);
            }

            if(_opts.error){
              _opts.error(req);
            }
        },
        complete: function() {
            if (_opts.complete) _opts.complete();
        }
    });
};

// ### BaseM.prototype.update
//
// Save the entity on the server.
//
// * `data` - the updated data to save
// * `opts` - optional callbacks: `success`, `error` and `fieldError`
BaseM.prototype.update = function(data, opts) {
    var _this = this,
        _updateUrl = this.data._links.update,
        _opts = opts || {};

    if(_updateUrl === undefined){
        ui.notification.showCustom(
            'error',
            sprintf(_this._core.translateString(_this._i18nKey, _this._i18nPrefix + ".msg.error-title"), _this.getDescription()),
            _this._core.translateString(_this._i18nKey, _this._i18nPrefix + ".msg.unauthorized-update")
        );

        if(_opts.error){
            _opts.error();
        }

        return;
    }

    let params = [];
    if(opts.acceptOutdated){
       params.push($.param({ acceptOutdated: true }));
    }
    if(opts.fix){
       params.push($.param({ fix: true }));
    }
    _updateUrl += '?' + params.join('&')

    $.ajax({
        url: _updateUrl,
        type: 'PUT',
        contentType: "application/json",
        data: JSON.stringify(data),
        success: function(responseData) {

            if (responseData && responseData._links) {
                // Updating our model with the one from the server
                _this.data = responseData;

            } else {
                // If everything went ok while updating the entity then we can use the current entity state
                // The entity will be updated the next time we make a search or when the entity is beeing edited.
                _this.data = data;
            }

            _this._core.publish(_this.update_event_name, _this.data);

            if (_opts.success) _opts.success();
        },
        error: function(req) {
            window.console.log("ENTITY:" + _this._entityKey + ":error-save: " +
                               req.status + " : " + req.statusText + ":" + req.responseText);

            if (req.status === 409) {
                ui.notification.showCustom(
                    'error',
                    sprintf(_this._core.translateString(_this._i18nKey, _this._i18nPrefix + ".msg.error-title"), _this.getDescription()),
                    _this._core.translateString("recordm.error.codes", "generic.409")
                );

            } else if (req.status === 400 && _opts.fieldError) {
                _opts.fieldError(JSON.parse(req.responseText));

            } else {
                ui.notification.showNotificationsFromRequest('error', req);
            }

            if(_opts.error){
              _opts.error(req);
            }
        },
        complete: function() {
            if (_opts.complete) _opts.complete();
        }
    });
};

// ### BaseM.prototype.deleteSelf
//
// Delete the entity on the server.
//
// * `opts` - optional callbacks: `success` and `error`
BaseM.prototype.deleteSelf = function(opts) {
    var _this = this,
        _opts = opts || {},
        _endpointPath;

    if (_this.data._links['delete']) {
        _endpointPath = _this.data._links['delete'];
    } else {
        _endpointPath = 'recordm/' + this._entityKey + '/' + this.data.id;
    }

    $.ajax({
        url: _endpointPath,
        type: "DELETE",
        success: function() {
            _this._core.publish(_this.delete_event_name, {entityKey: _this._entityKey, id: _this.data.id});

            _this.release();

            if (_opts.success) opts.success(_this.data.id);
        },
        error: function(req) {
            window.console.log("ENTITY:" + _this._entityKey + ":error-delete: " +
                               req.status + " : " + req.statusText + ":" + req.responseText);

            if (_opts.error) {
                if (req.status === 409) {
                    var reasons = JSON.parse(req.responseText).map(function(item) {
                        return ErrorType[item.errorType];
                    });

                    _opts.error(_this._core.translateString("recordm.error.codes", "error.delete.deleting"), reasons);

                } else if (req.status === 404) {
                    _opts.error(_this._core.translateString("recordm.error.codes", "error.delete.not-found"));

                } else {
                    _opts.error(_this._core.translateString("recordm.error.codes", "error.delete.unknown"));
                }
            }
        }
    });
};

// ### BaseM.prototype.release
//
// Tell the ModelRepository that we're finished with this entity.
BaseM.prototype.release = function() {
    this._core.release(this._entityKey + ":" + this.data.id);
};

// ## Permission stuff
BaseM.prototype.canUpdate = function() {
    return this.data._links.hasOwnProperty("update");
};

// ## Validation stuff

//Validates all entity fields and returns an errors array
//todo jone faz sentido receber uma entity externa? Não devia aplicar-se aos próprios dados?
BaseM.prototype.validate = function(entity) {
    var _this = this;
    var errors = [];

    utils.forEachOwn(entity, function(field) {
        errors = errors.concat(_this.validateField(field, entity[field]));
    });

    return errors;
};

BaseM.prototype._getValidationPropsFor = function(fieldName) {
    var len = this.constructor.fieldValidationProps.length,
        row;

    for (var i = 0; i < len; ++i) {
        row = this.constructor.fieldValidationProps[i];
        if (String(row.fieldName) === String(fieldName)) {
            return row.validationProps;
        }
    }

    window.console.log("ENTITY:WARN:no validation properties found for field " + this._entityKey + "." + fieldName);
    return undefined;
};

//Validates one field and returns an array (like the validate method) but with just the first error found for the field
//The reason to return an array, despite being only one value, is that it allows for the users of both methods to parse
// the response in the same manner.
//Only the first error found is returned because other errors could be misleading if caused by previous errors (also
// these one-field errors are usually shown next to the field and it normally has space for only one error)
//Note also that node validator throws an exception at the first error found (although that can be override if needed)
BaseM.prototype.validateField = function(fieldName, value) {
    var errorModel = new FieldErrorM(this._core);
    var validationProps = this._getValidationPropsFor(fieldName) || {};

    if (validationProps.mandatory && validator.isEmpty(value)) {
        errorModel.addError(fieldName, ErrorType.MANDATORY);
    }
    if (!utils.isNullOrEmpty(value)) {
        // We validate only non-empty fields since, if they are not required, we only want to check their values.
        // The node validator rejects an empty field if, for example, we are checking if it's a number.
        if (validationProps.dataType === BaseM.fieldDataType.NUMBER && !validator.isNumeric(value)) {
            errorModel.addError(fieldName, ErrorType.INVALID_FORMAT);
        }
        // **TODO jbarata** acrescentar aqui outas validações que sejam necessárias...
    }

    return errorModel.getTranslatedErrors();
};

// ## Private Helpers

// private helper method to centralize the json obj build of a field.
//
// Brief explanation about the arguments:
//
// * **fieldName**: the json field name.
// * **opts**: an options object with the following optional fields
//      - **label**: the label of the field.
//      - **columnWidth**: the starting column width. Default 100.
//      - **visibleByDefault**: if inicially appears in the slickgrid. Default true.
//      - **resizable**: if the column can be resized. It is usefull to fix the with on fields like telephone counters, ie, small text fields. Default true.
//      - **sortable**: if the column can be used for sorting. Default true.
//      - **align**: column text orientation. Supported values are "left, center". Default value is left.
//      - **possibleValues**: An array of values. Each array should have an object wiht label and value {'label': 'foo', 'value': 'bar'}
//      - **visibleInSearchResults**: If this property should appear in the specific entity search result
BaseM._buildEntityField = function(fieldName, opts) {
    // make the opts argument trully optional
    opts = opts || {};

    return {
        fieldName: fieldName,
        type: opts.type || BaseM.fieldDataType.STRING,
        label: opts.label || fieldName,
        labelIcon: opts.labelIcon,
        columnWidth: opts.columnWidth || 100,
        resizable: (opts.resizable === undefined) || opts.resizable,
        sortable: (opts.sortable === undefined) || opts.sortable,
        visibleByDefault: (opts.visibleByDefault === undefined) || opts.visibleByDefault,
        visible: (opts.visibleByDefault === undefined) || opts.visibleByDefault,
        formatter: opts.formatter,
        align: opts.align || "left",
        possibleValues: opts.possibleValues || [],
        visibleInSearchResults: (opts.visibleInSearchResults !== undefined) ? opts.visibleInSearchResults : true,
        fieldDefDescription: opts.fieldDefDescription || "",
        fieldDefId: opts.fieldDefId,
        printable: opts.printable || true
    };
};

//private helper method to centralize the json obj build of fieldValidationProperties
BaseM._buildFieldValidationProps = function(fieldName, mandatory, maxLength, dataType) {
    return {
        fieldName: fieldName,
        validationProps: {
            mandatory: mandatory,
            maxLength: maxLength,
            dataType: dataType || this.fieldDataType.STRING}
    };
};

// ## Public Helpers
BaseM.buildDeleteEventName = function(entityKey, id) {
    return "recordm:deleted:" + entityKey + ":" + id;
};

// ## Build the getColumn method for the Entity
//
// We have to do this since base methods are unable to access properties of the child type
// Function.bind would work but it isn't supported on IE8
//
BaseM.buildGetColumnFor = function(entityM) {
    var _this = entityM;

    return function(fieldName) {
        var column;

        _this.columns.forEach(function(col) {
            if (col.fieldName === fieldName) {
                column = col;
            }
        });

        return column;
    };
};

BaseM.prototype.getColumnValue = function(fieldName, applyFormatter) {
    var column = this.constructor.getColumn(fieldName),
        subColumns = fieldName.split("."),
        value = this.data[subColumns[0]],
        _applyFormatter = applyFormatter !== undefined ? applyFormatter : true;

    //If has subcolumns
    if (subColumns.length > 1) {
        for (var index = 1; index < subColumns.length; index++) {
            value = value[subColumns[index]];
        }
    }

    if (value === null || value === undefined) {
        return "";
    }

    if (column && column.formatter && _applyFormatter) {
        value = column.formatter(value, this.data);
    }

    return value;
};

export default BaseM;
